import React, { useEffect, useMemo, useState } from 'react';
import {
  RevenueHeroCard,
  KpiTileRow,
  WeeklyPerformance,
  TopProductsCard,
  SuccessBanner,
  ManageGoalsModal,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useAuth } from '../../../contexts/AuthContext';
import { useSalesGoalsStore } from '../../../store/salesGoalsStore';
import { GoalProgress } from '../../../services/salesGoalsApi';
import { RevenueHero, KpiTile, WeekBar, TopProduct } from './goalsTargetsTypes';
import styles from './SalesRepGoalsTargets.module.css';

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 1 : 2)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtRevenue(n: number): string {
  return `$${n.toLocaleString('en-US')}`;
}

function adaptHero(p: GoalProgress): RevenueHero {
  const remaining = Math.max(p.goal.target_revenue - p.achieved_revenue, 0);
  return {
    achieved: fmtRevenue(p.achieved_revenue),
    target: fmtRevenue(p.goal.target_revenue),
    achievedPct: `${p.achieved_revenue_pct}%`,
    remaining: fmtRevenue(remaining),
    remainingPct: p.achieved_revenue_pct,
  };
}

function adaptKpis(p: GoalProgress): KpiTile[] {
  return [
    {
      id: 'orders',
      label: 'Orders Created',
      current: `${p.achieved_orders}/${p.goal.target_orders}`,
      target: `${p.goal.target_orders}`,
      pct: p.achieved_orders_pct,
      color: 'blue',
      icon: 'orders',
    },
    {
      id: 'customers',
      label: 'New Customers Acquired',
      current: `${p.achieved_new_customers}`,
      target: `of ${p.goal.target_new_customers}`,
      pct: p.goal.target_new_customers > 0
        ? Math.round((p.achieved_new_customers / p.goal.target_new_customers) * 100)
        : 0,
      color: 'purple',
      icon: 'customers',
    },
    {
      id: 'visits',
      label: 'Customer Visits',
      current: `${p.achieved_visits}`,
      target: `of ${p.goal.target_visits}`,
      pct: p.achieved_visits_pct,
      color: 'pink',
      icon: 'upsell',
    },
  ];
}

function adaptWeeks(p: GoalProgress): WeekBar[] {
  const weeklyTarget = p.weeks.length > 0 ? p.goal.target_revenue / p.weeks.length : 0;
  return p.weeks.map((w) => {
    const ratio = weeklyTarget > 0 ? w.revenue / weeklyTarget : 0;
    const color: WeekBar['color'] =
      ratio >= 1.05 ? 'green' : ratio >= 0.85 ? 'blue' : ratio >= 0.6 ? 'orange' : 'red';
    return {
      id: `w${w.week_index}`,
      label: w.label,
      description: `${w.orders_count} orders`,
      current: w.revenue,
      target: weeklyTarget,
      amount: fmtRevenue(w.revenue),
      ofTarget: ` / ${fmtRevenue(weeklyTarget)}`,
      color,
    };
  });
}

function adaptTopProducts(p: GoalProgress): TopProduct[] {
  return p.top_products.map((t) => ({
    id: `tp${t.rank}`,
    rank: t.rank,
    name: t.product_name,
    ordersCount: `${t.orders_count} orders`,
    revenue: fmtMoney(t.revenue),
    growth: '',
  }));
}

const SalesRepGoalsTargets: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [showManageModal, setShowManageModal] = useState(false);

  const progress = useSalesGoalsStore((s) => s.progress);
  const loading = useSalesGoalsStore((s) => s.loading);
  const error = useSalesGoalsStore((s) => s.error);
  const fetchProgress = useSalesGoalsStore((s) => s.fetchProgress);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const hero = useMemo(() => (progress ? adaptHero(progress) : null), [progress]);
  const kpis = useMemo(() => (progress ? adaptKpis(progress) : []), [progress]);
  const weeks = useMemo(() => (progress ? adaptWeeks(progress) : []), [progress]);
  const products = useMemo(() => (progress ? adaptTopProducts(progress) : []), [progress]);

  const periodLabel = progress
    ? new Date(progress.goal.period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Performance Overview</h2>
          <p className={styles.subtitle}>📅 {periodLabel}</p>
        </div>
        {isAdmin && (
          <button type="button" className={styles.manageBtn} onClick={() => setShowManageModal(true)}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Manage Goals
          </button>
        )}
      </div>

      <AsyncBoundary
        loading={loading && !progress}
        error={error}
        empty={!loading && !progress}
        emptyMessage="No active goal for this period — ask your admin to set targets"
        onRetry={() => fetchProgress()}
      >
        {hero && <RevenueHeroCard data={hero} />}
        <KpiTileRow tiles={kpis} />
        <WeeklyPerformance weeks={weeks} />
        <TopProductsCard products={products} />
        <SuccessBanner />
      </AsyncBoundary>

      <ManageGoalsModal
        open={showManageModal}
        onClose={() => {
          setShowManageModal(false);
          fetchProgress().catch(() => undefined);
        }}
      />
    </div>
  );
};

export const SalesRepGoalsTargetsPage: React.FC = () => {
  return <SalesRepGoalsTargets />;
};

export { SalesRepGoalsTargets };
export default SalesRepGoalsTargets;
